
import { useState, useEffect } from "react";
import { fetchProductVariants, ProductVariant } from "@/services/admin/productVariantService";
import { supabase } from "@/integrations/supabase/client";

export const useProductVariants = (productId: string) => {
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  const [selectedPotType, setSelectedPotType] = useState<string>("");
  const [selectedSize, setSelectedSize] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadVariants = async () => {
    if (!productId) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchProductVariants(productId);
      setVariants(data);
      const defaultVariant = data.find(v => v.is_default) || data[0];
      if (defaultVariant) {
        setSelectedVariant(defaultVariant);
        setSelectedPotType(defaultVariant.pot_type);
        setSelectedSize(defaultVariant.size);
      } else {
        setSelectedVariant(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load variants");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadVariants();
  }, [productId]);

  useEffect(() => {
    // Update selected variant when pot type or size changes
    const variant = variants.find(
      v => v.pot_type === selectedPotType && v.size === selectedSize
    );
    setSelectedVariant(variant || null);
  }, [variants, selectedPotType, selectedSize]);

  // Realtime updates disabled in edit mode to prevent dialog closing
  // Changes will be visible after save and reload
  // useEffect(() => {
  //   if (!productId) return;
  //   const channelName = `variants-${productId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  //   const channel = supabase
  //     .channel(channelName)
  //     .on('postgres_changes', { event: '*', schema: 'public', table: 'product_variants', filter: `product_id=eq.${productId}` }, () => { loadVariants(); })
  //     .subscribe();
  //   return () => { supabase.removeChannel(channel); };
  // }, [productId]);

  const availablePotTypes = [...new Set(variants.map(v => v.pot_type))];
  const availableSizes = selectedPotType 
    ? [...new Set(variants.filter(v => v.pot_type === selectedPotType).map(v => v.size))]
    : [];

  return {
    variants,
    selectedVariant,
    setSelectedVariant,
    selectedPotType,
    selectedSize,
    setSelectedPotType,
    setSelectedSize,
    availablePotTypes,
    availableSizes,
    isLoading,
    error,
  };
};
